import React, { useState, useEffect } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import HourlyPlatesStackedChart from "../components/HourlyPlatesStackedChart";
import InsightsPanel from "../components/InsightsPanel";
import { authFetch } from "../utils/authFetch";
import PassingsExportForm from "./PassingsExportForm";
import API_ENDPOINTS from "../apiConfig";
import { useSite } from "../context/SiteContext";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import { getLanguage } from '../utils/localStorageHelper';
import { contrastColor } from '../utils/colorUtils';
import { normalizePlate, findFuzzyPlateMatch } from '../utils/plateMatch';

const CHART_COLORS = ["#fb923c", "#10b981", "#3b82f6", "#8b5cf6", "#06b6d4"];

function StatisticDetails() {
    const { selectedSite, areas: loadedAreas, siteId, sites, loadingSites, setLoadingSites, platesByArea } = useSite();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [rawStats, setRawStats] = useState(null); // Stores the full 7-day JSON
    const [chartData, setChartData] = useState([]);
    const [areaNames, setAreaNames] = useState([]);
    const [loading, setLoading] = useState(true);

    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
    const [selectedAreaLocal, setSelectedAreaLocal] = useState("all");

    const [clickedSlot, setClickedSlot] = useState(null);
    const [hourPassings, setHourPassings] = useState(null);
    const [loadingPassings, setLoadingPassings] = useState(false);
    const [showAuthorized, setShowAuthorized] = useState(true);
    const [showUnauthorized, setShowUnauthorized] = useState(true);

    const [showExport, setShowExport] = useState(false);

    const actualUserRole = localStorage.getItem("actualUserRole");
    const canExport = actualUserRole === "site_manager" || actualUserRole === "area_manager";

    // last 7 days for the button sidebar
    const lastDates = Array.from({ length: 7 }, (_, i) => {
        const d = new Date();
        d.setDate(d.getDate() - i);
        return d.toISOString().split("T")[0];
    });

    const selectedDateIdx = lastDates.indexOf(selectedDate);
    const goToPrevDay = () => { if (selectedDateIdx < lastDates.length - 1) setSelectedDate(lastDates[selectedDateIdx + 1]); };
    const goToNextDay = () => { if (selectedDateIdx > 0) setSelectedDate(lastDates[selectedDateIdx - 1]); };

    // 1. DATA FETCHING: Only runs when the selected site changes
    useEffect(() => {
        if (siteId == null) return;

        const fetchStats = async () => {
            setLoading(true);
            try {
                const res = await authFetch(`${API_ENDPOINTS.getsites}/${siteId}/stats`);

                const json = await res.json();
                setRawStats(json); // Save the whole object to state
                setLoading(false);
            } catch (err) {
                console.error(err);
                setLoading(false);
            }
        };

        fetchStats();
    }, [siteId]); // Only site-change triggers a network request

    // 2. LOCAL TRANSFORMATION: Runs when Date or Area selection changes
    useEffect(() => {
        if (!rawStats) return;
        setClickedSlot(null);
        setHourPassings(null);

        // map id → area name
        const areaMap = {};
        loadedAreas.forEach(a => { areaMap[a.id] = a.name; });

        const areasToShow = selectedAreaLocal === "all" ? Object.keys(rawStats) : [selectedAreaLocal];

        const data = Array.from({ length: 24 }, (_, hour) => {
            const obj = { hour: `${hour}:00` };

            areasToShow.forEach(areaId => {
                const areaName = areaMap[areaId] || `Area ${areaId}`;
                const dayData = rawStats[areaId]?.[selectedDate] || [];

                obj[`${areaName}_allowed`] = dayData[hour]?.y || 0;
                obj[`${areaName}_notAllowed`] = dayData[hour]?.n || 0;
            });

            return obj;
        });

        setChartData(data);
        setAreaNames(areasToShow.map(id => areaMap[id] || `Area ${id}`));
    }, [rawStats, selectedDate, selectedAreaLocal, loadedAreas]);

    if (siteId == null) {
        return <Navigate to="/dashboard" replace />;
    }

    if (loading) return <p>{t("Loading statistics...")}</p>;
    if (loadingSites) return <p>{t("Loading sites...")}</p>;


    // Calculate totals for the summary header based on the current visible data
    const totals = chartData.reduce((acc, hour) => {
        Object.keys(hour).forEach(key => {
            if (key.includes("_allowed")) acc.allowed += hour[key];
            if (key.includes("_notAllowed")) acc.blocked += hour[key];
        });
        return acc;
    }, { allowed: 0, blocked: 0 });

    // Stable color map: keyed by area name, ordered by rawStats key order
    const areaColorMap = rawStats
        ? Object.keys(rawStats).reduce((map, areaId, idx) => {
            const name = loadedAreas.find(a => String(a.id) === String(areaId))?.name || `Area ${areaId}`;
            map[name] = CHART_COLORS[idx % CHART_COLORS.length];
            return map;
        }, {})
        : {};

    const handleHourClick = async (areaName, hour) => {
        const isAllAreas = selectedAreaLocal === "all";
        const area = isAllAreas ? null : loadedAreas.find(a => a.name === areaName);
        const areaId = area?.id ?? null;
        const displayName = isAllAreas ? t("All areas") : areaName;

        setClickedSlot({ areaId, areaName: displayName, hour });
        setHourPassings(null);
        setLoadingPassings(true);

        const url = new URL(API_ENDPOINTS.getHourPassings.replace('##siteid##', siteId));
        url.searchParams.set('date', selectedDate);
        url.searchParams.set('hour', hour);
        if (areaId) url.searchParams.set('areaId', areaId);

        try {
            const res = await authFetch(url.toString());
            setHourPassings(await res.json());
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingPassings(false);
        }
    };

    const dateButtons = lastDates.map(date => (
        <button
            key={date}
            className={`btn btn-sm ${selectedDate === date ? "ez-button-full" : "ez-button-light"}`}
            onClick={() => setSelectedDate(date)}
        >
            {new Date(date).toLocaleDateString(getLanguage(), {
                weekday: 'short',
                day: '2-digit',
                month: '2-digit'
            })}
        </button>
    ));

    const areaSelect = (
        <select
            className="form-select w-auto"
            value={selectedAreaLocal}
            onChange={e => setSelectedAreaLocal(e.target.value)}
        >
            <option value="all">{t("All areas")}</option>
            {loadedAreas.map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
            ))}
        </select>
    );

    const chartBlock = (
        <>
            <div className="d-flex justify-content-between align-items-end mb-3 border-bottom pb-2">
                <h5 className="mb-0 text-muted">
                    {selectedAreaLocal === "all" ? t("Site Overview") : areaNames[0]}
                </h5>
                <div className="h4 mb-0">
                    <span className="me-3">
                        <small style={{ fontSize: '0.6em' }} className="text-muted uppercase">{t("Authorized")}: </small>
                        <span style={{ color: "var(--ez-orange-light)" }} className="fw-bold">{totals.allowed}</span>
                    </span>
                    <span>
                        <small style={{ fontSize: '0.6em' }} className="text-muted uppercase">{t("Unauthorized")}: </small>
                        <span className="text-danger fw-bold">{totals.blocked}</span>
                    </span>
                </div>
            </div>

            <HourlyPlatesStackedChart
                data={chartData}
                areas={areaNames}
                areaColorMap={areaColorMap}
                selectedHour={clickedSlot?.hour}
                onHourClick={handleHourClick}
                onLegendClick={(areaName) => {
                    const area = loadedAreas.find(a => a.name === areaName);
                    if (area) setSelectedAreaLocal(String(area.id));
                }}
            />
        </>
    );

    return (
        <div className="container mt-4">
            <div className="mb-4 d-flex align-items-center gap-3">
                <button className="btn btn-sm btn-light" onClick={() => navigate('/dashboard')}>
                    <i className="bi bi-arrow-left"></i>
                </button>
                <h3 className="mb-0">{selectedSite.name}</h3>
                <span className="text-muted">|</span>
                <h5 className="mb-0">{t("Statistics")}</h5>
                {canExport && (
                    <button
                        className="btn btn-sm ez-button-light ms-auto"
                        onClick={() => setShowExport(v => !v)}
                    >
                        <i className="bi bi-download me-1"></i>
                        <span className="d-none d-sm-inline">{t("export_csv")}</span>
                    </button>
                )}
            </div>

            {canExport && showExport && (
                <div className="mb-4 p-3 border ez-rounded-tile ez-shadow bg-white">
                    <PassingsExportForm
                        siteId={siteId}
                        siteName={selectedSite?.name}
                        areas={loadedAreas}
                        areaId={selectedAreaLocal}
                        onDone={() => setShowExport(false)}
                    />
                </div>
            )}

            {/* Mobile: area dropdown only — date handled by sticky bottom nav */}
            <div className="d-flex d-md-none gap-2 mb-3">
                {areaSelect}
            </div>

            {/* Desktop: area dropdown only (date handled by sidebar buttons) */}
            <div className="d-none d-md-flex gap-2 mb-3 align-items-center">
                <label className="form-label me-2">{t("select_area")}:</label>
                {areaSelect}
            </div>

            <InsightsPanel selectedAreaLocal={selectedAreaLocal} />

            {/* Chart + drill-down: one unified card */}
            <div className="p-4 border ez-rounded-card bg-white shadow-sm mt-3">

                {/* Mobile: chart full-width */}
                <div className="d-md-none">
                    {chartBlock}
                </div>

                {/* Desktop: vertical sidebar + chart */}
                <div className="row d-none d-md-flex">
                    <div className="col-auto d-flex flex-column gap-2">
                        {dateButtons}
                    </div>
                    <div className="col" style={{ minWidth: 0 }}>
                        {chartBlock}
                    </div>
                </div>

                {/* Mobile: day navigation */}
                <div className="d-md-none d-flex justify-content-between align-items-center mt-3 px-2 py-2 rounded" style={{ backgroundColor: 'var(--ez-page-bg)' }}>
                    <button className="btn btn-sm bg-white border ez-shadow" onClick={goToPrevDay} disabled={selectedDateIdx >= lastDates.length - 1}>
                        <i className="bi bi-chevron-left" />
                    </button>
                    <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                        {new Date(selectedDate).toLocaleDateString(getLanguage(), { weekday: 'short', day: '2-digit', month: '2-digit' })}
                    </span>
                    <button className="btn btn-sm bg-white border ez-shadow" onClick={goToNextDay} disabled={selectedDateIdx <= 0}>
                        <i className="bi bi-chevron-right" />
                    </button>
                </div>

                {/* Hour drill-down — separated within the same card */}
                <div className="mt-4 pt-3 border-top">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">
                        {clickedSlot ? (
                            <>
                                <span className="text-muted">{clickedSlot.hour}:00</span>
                                <span className="mx-2">—</span>
                                {clickedSlot.areaName}
                            </>
                        ) : (
                            <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                                <i className="bi bi-hand-index me-1" />{t("click_hour_hint")}
                            </span>
                        )}
                    </h6>
                    {clickedSlot && (
                        <button className="btn btn-sm btn-light" onClick={() => { setClickedSlot(null); setHourPassings(null); }}>
                            <i className="bi bi-x" />
                        </button>
                    )}
                </div>

                {loadingPassings && <p className="text-muted">{t("Loading...")}</p>}

                {/* Authorized section */}
                <div className="mb-2 rounded" style={{ overflow: 'hidden' }}>
                    <div
                        className="d-flex align-items-center px-3 py-2 mb-1 rounded"
                        style={{ background: "#10b98118", borderLeft: "4px solid #10b981", cursor: clickedSlot ? "pointer" : "default", userSelect: "none" }}
                        onClick={() => clickedSlot && setShowAuthorized(v => !v)}
                    >
                        <i className={`bi bi-chevron-${showAuthorized ? 'down' : 'right'} me-2`} style={{ color: "#10b981", fontSize: "0.8rem", visibility: clickedSlot ? 'visible' : 'hidden' }} />
                        <span className="fw-semibold" style={{ color: "#10b981", fontSize: "0.85rem" }}>✓ {t("Authorized")}</span>
                        {hourPassings && (
                            <span className="ms-2 rounded-pill px-2" style={{ fontSize: "0.72rem", background: "#10b98133", color: "#10b981", fontWeight: 600 }}>
                                {hourPassings.authorized.length}
                            </span>
                        )}
                    </div>
                    {clickedSlot && showAuthorized && hourPassings && (() => {
                        const allPlates = clickedSlot.areaId === null
                            ? Object.values(platesByArea).flat()
                            : (platesByArea[clickedSlot.areaId] ?? []);
                        // exact match first (cheap, common case); fall back to fuzzy only on a miss —
                        // the camera already fuzzy-matched to open the gate, this just closes the gap
                        // where our own display-side lookup was exact-only and showed no enrichment at all.
                        // Whenever we do find a match (exact or fuzzy), show the enrolled plate's own
                        // canonical text instead of the raw OCR reading — misreads stay fully invisible,
                        // except a small marker for global/partner scope users (see actualUserScope).
                        const enrich = (plate_no) => {
                            const exact = allPlates.find(p => normalizePlate(p.plate) === normalizePlate(plate_no));
                            if (exact) return { detail: exact, fuzzy: false };
                            const match = findFuzzyPlateMatch(plate_no, allPlates);
                            return match ? { detail: match.candidate, fuzzy: true } : { detail: null, fuzzy: false };
                        };
                        const actualUserScope = localStorage.getItem("actualUserScope");
                        const canSeeFuzzyMarker = actualUserScope === "global" || actualUserScope === "partner";
                        return (
                            <div className="px-2 pb-2">
                                {hourPassings.authorized.length === 0
                                    ? <small className="text-muted">—</small>
                                    : hourPassings.authorized.map((entry, i) => {
                                        const { detail, fuzzy } = enrich(entry.plate_no);
                                        return (
                                            <div key={i} className="d-flex align-items-center gap-2 py-1 border-bottom">
                                                <span className="fw-bold">{detail ? detail.plate : entry.plate_no}</span>
                                                {entry.is_pss_match && (
                                                    <small className="text-muted" title={t("pss_match_hint")}>
                                                        <i className="bi bi-shield-check"></i>
                                                    </small>
                                                )}
                                                {fuzzy && detail && canSeeFuzzyMarker && (
                                                    <OverlayTrigger
                                                        trigger={["hover", "click"]}
                                                        placement="top"
                                                        rootClose
                                                        overlay={<Tooltip>{t("fuzzy_match_hint", { raw: entry.plate_no })}</Tooltip>}
                                                    >
                                                        <small className="text-muted" style={{ cursor: "pointer" }}>
                                                            <i className="bi bi-question-circle"></i>
                                                        </small>
                                                    </OverlayTrigger>
                                                )}
                                                {detail?.note && <small className="text-muted">{detail.note}</small>}
                                                {detail?.categories?.map(c => (
                                                    <span key={c.id} className="badge rounded-pill" style={{ backgroundColor: c.color, color: contrastColor(c.color), fontSize: '0.75rem' }}>{c.name}</span>
                                                ))}
                                                <small className="text-muted ms-auto">{entry.time}</small>
                                            </div>
                                        );
                                    })}
                            </div>
                        );
                    })()}
                </div>

                {/* Unauthorized section */}
                <div className="rounded" style={{ overflow: 'hidden' }}>
                    <div
                        className="d-flex align-items-center px-3 py-2 mb-1 rounded"
                        style={{ background: "#ef444418", borderLeft: "4px solid #ef4444", cursor: clickedSlot ? "pointer" : "default", userSelect: "none" }}
                        onClick={() => clickedSlot && setShowUnauthorized(v => !v)}
                    >
                        <i className={`bi bi-chevron-${showUnauthorized ? 'down' : 'right'} me-2`} style={{ color: "#ef4444", fontSize: "0.8rem", visibility: clickedSlot ? 'visible' : 'hidden' }} />
                        <span className="fw-semibold" style={{ color: "#ef4444", fontSize: "0.85rem" }}>✗ {t("Unauthorized")}</span>
                        {hourPassings && (
                            <span className="ms-2 rounded-pill px-2" style={{ fontSize: "0.72rem", background: "#ef444433", color: "#ef4444", fontWeight: 600 }}>
                                {hourPassings.unauthorized.length}
                            </span>
                        )}
                    </div>
                    {clickedSlot && showUnauthorized && hourPassings && (
                        <div className="px-2 pb-2">
                            {hourPassings.unauthorized.length === 0
                                ? <small className="text-muted">—</small>
                                : hourPassings.unauthorized.map((entry, i) => (
                                    <div key={i} className="d-flex align-items-center gap-2 py-1 border-bottom">
                                        <span className="fw-bold text-danger">{entry.plate_no}</span>
                                        <small className="text-muted ms-auto">{entry.time}</small>
                                    </div>
                                ))}
                        </div>
                    )}
                </div>
                </div> {/* end drill-down */}
            </div> {/* end unified card */}

        </div>
    );
}

export default StatisticDetails;
